// F13c-1 / SD42 web half: shared SSO sign-in helpers (SignIn, SignUp, admin SSO settings).
// Server counterparts: sso::enforcement_blocks_email (domain-scoped force-SSO),
// sso::sso_may_sign_in_as (account-takeover guard) and oauth::callback's sso_required redirect.

/** `/auth/sso/{slug}/start` isn't a route this SPA owns: it's a server redirect into the IdP
 * (sso.rs), so callers do a full-page navigation to it, not a `navigate()` call. */
export function ssoStartUrl(orgSlug: string): string {
  return `/api/v1/auth/sso/${encodeURIComponent(orgSlug)}/start`;
}

/** Copy for `?sso_error=` codes the server redirects back to /signin with. */
export const SSO_ERROR_MESSAGES: Record<string, string> = {
  exchange_failed: "Something went wrong completing sign-in with your organization's SSO provider.",
  access_denied: "Sign-in was cancelled.",
  // oauth::callback: "continue with Google/Microsoft" for an address at a verified domain whose
  // org requires SSO (FR-S6/FR-O3, SD42).
  sso_required:
    "Your organization requires single sign-on for this email address. Continue with SSO instead.",
  // sso::callback (SD42 hardening): the IdP asserted the email of an existing Grackle account that
  // isn't a member of the org and isn't at a domain the org has verified.
  account_outside_org:
    "Your organization's SSO can't sign in to this Grackle account: it isn't a member of the organization and its email domain isn't verified by it. Sign in another way, or ask an admin to invite this account.",
};

export function ssoErrorMessage(code: string | null): string | null {
  if (!code) return null;
  return SSO_ERROR_MESSAGES[code] ?? "Sign-in with SSO didn't complete.";
}

/** `?sso_error=sso_required&org_slug=…` (oauth::callback) → the org slug to continue with, else null. */
export function ssoRequiredSlugFromParams(params: URLSearchParams): string | null {
  if (params.get("sso_error") !== "sso_required") return null;
  const slug = params.get("org_slug")?.trim();
  return slug ? slug : null;
}

/** Lowercased domain part of an email address, or null when there isn't one. */
export function emailDomain(email: string): string | null {
  const at = email.trim().toLowerCase().lastIndexOf("@");
  if (at < 0) return null;
  const domain = email.trim().toLowerCase().slice(at + 1);
  return domain ? domain : null;
}

/** Verified domains only, lowercased, sorted: the ones force-SSO reads (SD42). */
export function verifiedDomains(domains: { domain: string; verified_at: string | null }[]): string[] {
  return domains
    .filter((d) => d.verified_at)
    .map((d) => d.domain.trim().toLowerCase())
    .sort();
}

/** Would force-SSO cover `email`? Mirrors the server's exact-match rule (SD42): verifying
 * `acme.com` covers `@acme.com` only, never `@eng.acme.com` (subdomains need their own row). */
export function domainCoversEmail(email: string, verified: string[]): boolean {
  const domain = emailDomain(email);
  if (!domain) return false;
  return verified.some((d) => d.trim().toLowerCase() === domain);
}
